import Popper from 'vue-popperjs';
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import { BfButton } from 'src/components/Butterfly';
import ProjectAPI from 'src/api/project';
import Query from 'src/api/query';
import { CLEAR_REQUEST_ERRORS, FETCH_PROJECTS } from 'src/store/types';
import store from 'src/store';
const CopyQueries = Vue.extend({
    components: { BfButton, Popper },
    beforeRouteEnter(to, from, next) {
        if (store?.getters?.currentUser?.viewer) {
            next({ name: 'viewer-home', params: { site: to.params.site } });
        }
        else {
            next();
        }
    },
    data() {
        return {
            copyFailed: false,
            schemaMismatch: false,
            isCopying: false,
            sourceProject: null,
            sourceAnalysis: null,
            sourceQueries: null,
            destinationProject: null,
            destinationAnalysis: null,
            destinationQueries: null,
            overwriteExistingQueries: false,
            deleteExistingQueries: false,
            skipStructured: false,
            addToDashboards: true,
            copyCompleted: false,
            results: null,
            projectList: {
                source: [],
                destination: [],
            },
            loading: {
                source: false,
                destination: false,
            },
            selectedQueries: [],
        };
    },
    computed: {
        ...mapGetters([
            'hasRequestErrors', 'serverErrors', 'validationErrors', 'projects', 'currentUser',
            'featureFlags',
        ]),
        sourceAnalyses() {
            return this.sourceProject?.analyses.slice()
                .sort((a1, a2) => a1.name.localeCompare(a2.name)) || [];
        },
        sourceQueriesTooltip() {
            return this.generateQueriesTooltip(this.sourceQueries || []);
        },
        destinationAnalyses() {
            return this.destinationProject?.analyses.slice()
                .sort((a1, a2) => a1.name.localeCompare(a2.name)) || [];
        },
        destinationQueriesTooltip() {
            return this.generateQueriesTooltip(this.destinationQueries || []);
        },
        projectsSorted() {
            return this.projects.slice().sort((p1, p2) => p1.name.localeCompare(p2.name));
        },
        destinationRoute() {
            return {
                name: 'view-analysis',
                params: {
                    projectId: this.destinationProject?.id,
                    analysisId: this.destinationAnalysis?.id,
                }
            };
        },
        themeSelectTitle() {
            return `${this.selectedQueries?.length} themes selected`;
        }
    },
    watch: {
        sourceProject() {
            this.sourceAnalysis = null;
        },
        sourceAnalysis() {
            this.loadSourceQueries();
        },
        destinationProject() {
            this.destinationAnalysis = null;
        },
        destinationAnalysis() {
            this.loadDestinationQueries();
        }
    },
    async mounted() {
        try {
            await this.FETCH_PROJECTS();
        }
        finally {
            this.projectList.source = this.projectsSorted;
            this.projectList.destination = this.projectsSorted;
        }
    },
    methods: {
        ...mapActions({ FETCH_PROJECTS }),
        async searchForProjects(query, target) {
            if (query === '') {
                this.projectList[target] = this.projectsSorted;
                return;
            }
            try {
                this.loading[target] = true;
                const response = await ProjectAPI.getProjects(1, query, "name");
                this.projectList[target] = response.results;
            }
            catch (e) {
                this.projectList[target] = this.projectsSorted;
            }
            finally {
                setTimeout(() => {
                    this.loading[target] = false;
                }, 500);
            }
        },
        async doCopy() {
            if (!this.sourceAnalysis || !this.destinationAnalysis) {
                return;
            }
            this.isCopying = true;
            try {
                const resp = await ProjectAPI.copyQueries(this.sourceAnalysis.id, this.destinationAnalysis.id, this.deleteExistingQueries, this.addToDashboards, this.skipStructured, this.selectedQueries, this.overwriteExistingQueries);
                this.copyCompleted = true;
                this.results = resp.data;
                // Track event
                this.$analytics.track.site.copyQueries(this.sourceAnalysis, this.destinationAnalysis, this.deleteExistingQueries, this.skipStructured, this.addToDashboards, this.selectedQueries, this.overwriteExistingQueries, {
                    queriesCopied: this.results.queries_copied,
                    duplicateQueriesSkipped: this.results.duplicate_queries_skipped,
                    structuredQueriesSkipped: this.results.structured_queries_skipped
                });
            }
            catch (e) {
                this.$store.dispatch(CLEAR_REQUEST_ERRORS); // use custom instead of global error handling
                if (e.body.non_field_errors?.toLowerCase().indexOf("schema mismatch") >= 0) {
                    this.schemaMismatch = true;
                    if (e?.body?.structured_queries?.length > 0) {
                        this.results = e.body.structured_queries;
                    }
                }
                else {
                    this.copyFailed = true;
                }
            }
        },
        async doRetry(skipStructured = false) {
            if (skipStructured) {
                this.skipStructured = true;
            }
            this.copyFailed = false;
            this.schemaMismatch = false;
            this.doCopy();
        },
        generateQueriesTooltip(queries, numQueriesToShow = 10) {
            if (queries) {
                const topQueries = queries.slice(0, numQueriesToShow);
                let tooltipStr = topQueries.map(q => q.name).join('<br>');
                if (queries.length > numQueriesToShow) {
                    tooltipStr += `<br><i>And ${queries.length - numQueriesToShow} other queries...</i>`;
                }
                return tooltipStr;
            }
            return '';
        },
        goBack(resetSelections = false) {
            if (resetSelections) {
                this.sourceProject = null;
                this.destinationProject = null;
                this.overwriteExistingQueries = false;
                this.deleteExistingQueries = false;
                this.skipStructured = false;
            }
            this.isCopying = false;
            this.copyCompleted = false;
            this.copyFailed = false;
            this.schemaMismatch = false;
            this.loadSourceQueries();
            this.loadDestinationQueries();
        },
        async loadSourceQueries() {
            if (this.sourceProject && this.sourceAnalysis) {
                this.sourceQueries = await Query.listSavedQueries(this.sourceProject.id, this.sourceAnalysis.id);
                this.selectedQueries = this.sourceQueries.map(q => q.id);
            }
            else {
                this.sourceQueries = null;
                this.selectedQueries = null;
            }
        },
        async loadDestinationQueries() {
            if (this.destinationProject && this.destinationAnalysis) {
                this.destinationQueries = await Query.listSavedQueries(this.destinationProject.id, this.destinationAnalysis.id);
            }
            else {
                this.destinationQueries = null;
            }
        },
        selectAllQueries() {
            this.selectedQueries = this.sourceQueries.map(q => q.id);
        },
        deselectAllQueries() {
            this.selectedQueries = [];
        },
        changeExistingQueries(optionExistingQueries) {
            if (optionExistingQueries === 'overwriteExistingQueries' && this.deleteExistingQueries === true) {
                this.deleteExistingQueries = false;
            }
            if (optionExistingQueries === 'deleteExistingQueries' && this.overwriteExistingQueries === true) {
                this.overwriteExistingQueries = false;
            }
        },
    }
});
export default CopyQueries;
